/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Sync section component - handles sync with main and conflict alerts
 */

import React from 'react';
import { RefreshCw, GitMerge, AlertTriangle, Loader, Sparkles } from 'lucide-react';
import type { ConflictInfo } from '../conflicts';

interface SyncSectionProps {
  onSync: () => void;
  syncing: boolean;
  onCheckConflicts: () => void;
  checkingConflicts: boolean;
  hasConflicts: boolean;
  conflicts: ConflictInfo[];
  onShowConflictDialog: () => void;
}

export function SyncSection({
  onSync,
  syncing,
  onCheckConflicts,
  checkingConflicts,
  hasConflicts,
  conflicts,
  onShowConflictDialog
}: SyncSectionProps) {
  return (
    <div className="p-4 space-y-2 border-b border-gray-200">
      <div className="flex gap-2">
        <button
          onClick={onSync}
          disabled={syncing}
          className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-1"
        >
          {syncing ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          Sync with Main
        </button>
        <button
          onClick={onCheckConflicts}
          disabled={checkingConflicts}
          className="btn-secondary text-sm py-2 px-3"
          title="Check for conflicts"
        >
          {checkingConflicts ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <GitMerge size={14} />
          )}
        </button>
      </div>
      
      {/* Conflict Alert */}
      {hasConflicts && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">
                Merge Conflicts Detected
              </p>
              <p className="text-xs text-orange-700 mt-1">
                {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} need resolution
              </p>
              <button
                onClick={onShowConflictDialog}
                className="mt-2 w-full btn-primary text-xs py-1.5 flex items-center justify-center gap-1"
              >
                <Sparkles size={12} />
                Resolve Conflicts
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

